import {
  AppCard,
  ChipList,
  Icon,
  PageContainer,
  RouteIconButtonLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiEye } from "@mdi/js";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  prefetchListBrandsQuery,
  useListBrandsSuspense,
} from "../../../../__generated__/orval/endpoints/brands/brands";
import type { EntitiesBrandListResponse } from "../../../../__generated__/orval/models/entitiesBrandListResponse.zod";

import { ForbiddenError } from "../../../../errors/ForbiddenError";
import { useAuth } from "../../../../hooks/useAuth";
import { ExclusiveBrandFilters } from "./-internal_components/ExclusiveBrandFilters";

const searchSchema = z.object({
  hasExclusiveVendors: z.optional(z.boolean()).catch(undefined),
  name_like: z.optional(z.string()).catch(undefined),
  page: z.coerce.number().int().min(1).default(1).catch(1),
  rowsPerPage: z.coerce.number().int().min(1).default(25).catch(25),
  vendorId: z.coerce.number().int().positive().optional().catch(undefined),
});

type BrandItem = EntitiesBrandListResponse["results"][number];

const ExclusiveBrandTableRow: React.FC<{
  brand: BrandItem;
}> = ({ brand }) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  return (
    <TableRow key={brand.id}>
      <TableCell>{brand.name}</TableCell>
      <TableCell>
        <ChipList
          fieldLabel={t("common.vendor")}
          items={
            brand.exclusive_vendors?.map((vendor) => ({
              id: vendor.id,
              label: vendor.name,
              modalLabel: vendor.name_with_sap_code_and_tax_number,
            })) || []
          }
          limit={2}
        />
      </TableCell>
      {user?.roles_names.includes("Brand Exclusive - Update") && (
        <TableCell align="right">
          <Tooltip title={t("common.view")}>
            <RouteIconButtonLink
              params={{ id: String(brand.id) }}
              to="/vsupplierregistration/exclusive_brands/$id"
            >
              <Icon icon={mdiEye} />
            </RouteIconButtonLink>
          </Tooltip>
        </TableCell>
      )}
    </TableRow>
  );
};

const ExclusiveBrandTable: React.FC = () => {
  const navigate = Route.useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const { page = 1, rowsPerPage = 25 } = Route.useSearch();

  const loaderDeps = Route.useLoaderDeps();
  const { data: brandsResponse } = useListBrandsSuspense(loaderDeps);

  useEffect(() => {
    if (!brandsResponse) return;

    const { current_page: currentPage, items_per_page: currentItemsPerPage } =
      brandsResponse.pagination;

    if (currentPage !== page || currentItemsPerPage !== rowsPerPage) {
      navigate({
        replace: true,
        search: (prev) => ({
          ...prev,
          page: currentPage,
          rowsPerPage: currentItemsPerPage,
        }),
      });
    }
  }, [brandsResponse, page, rowsPerPage, navigate]);

  const handleChangePage = useCallback(
    (_event: unknown, newPage: number) => {
      navigate({
        search: (prev) => ({
          ...prev,
          page: newPage + 1,
        }),
      });
    },
    [navigate],
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      navigate({
        search: (prev) => ({
          ...prev,
          page: 1,
          rowsPerPage: parseInt(event.target.value, 10),
        }),
      });
    },
    [navigate],
  );

  const brands = useMemo(() => brandsResponse?.results || [], [brandsResponse]);

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t("common.brandName")}</TableCell>
            <TableCell>{t("common.vendor")}</TableCell>
            {user?.roles_names.includes("Brand Exclusive - Update") && (
              <TableCell align="right" />
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {!brands.length ? (
            <TableRow>
              <TableCell
                align="center"
                colSpan={
                  user?.roles_names.includes("Brand Exclusive - Update") ? 3 : 2
                }
              >
                <Typography
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  {t("common.noBrandsFound")}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            brands.map((brand) => (
              <ExclusiveBrandTableRow brand={brand} key={brand.id} />
            ))
          )}
        </TableBody>
        {!!brandsResponse && (
          <TableFooter>
            <TableRow>
              <TablePagination
                count={brandsResponse.total_items}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                page={page - 1}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                showFirstButton
                showLastButton
              />
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
};

const ExclusiveBrand: React.FC = () => {
  const { t } = useTranslation();

  return (
    <PageContainer
      maxWidth="xl"
      showBackButton={false}
      title={t("common.exclusiveBrand")}
    >
      <AppCard>
        <Suspense
          fallback={
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress size={24} sx={{ margin: 2 }} />
            </Box>
          }
        >
          <ExclusiveBrandFilters />
        </Suspense>

        <Suspense
          fallback={
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress size={24} sx={{ margin: 2 }} />
            </Box>
          }
        >
          <ExclusiveBrandTable />
        </Suspense>
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vsupplierregistration/exclusive_brands/",
  // biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
)({
  validateSearch: searchSchema,
  component: ExclusiveBrand,
  loaderDeps: ({ search }) => ({
    exclusive_vendor: search.vendorId?.toString(),
    has_exclusive_vendors: search.hasExclusiveVendors,
    items_per_page: search.rowsPerPage,
    name_like: search.name_like,
    "order[name]": "asc" as const,
    page: search.page,
  }),
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Brand Exclusive - View")) {
      return;
    }

    throw new ForbiddenError();
  },
  loader: ({ context, deps }) => {
    prefetchListBrandsQuery(context.queryClient, deps);

    return null;
  },
});
